import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../../../store/store';
import useHistory from '../../../../../hooks/useHistory';
import { API_ENDPOINTS } from '../../../../../api/endpoints';
import ProfilerNameSearch from '../shared/ProfilerNameSearch';
import IndividualsNameResultItem from './IndividualsNameResultItem';
import { updateUrlSearchQuery } from '../shared/profilerNameSearchUtils';

/**
 * IndividualsNameSearch - Individual name search using shared ProfilerNameSearch
 *
 * Wraps ProfilerNameSearch with individuals-specific configuration:
 * - Uses individuals API endpoint
 * - Renders IndividualsNameResultItem for each result
 * - TD-1709: Uses URL-based navigation to standalone individual details page
 *   (no data preload - the standalone page fetches full data from API)
 *
 * @param {string} inputId - Unique ID for the input element (default: 'ps-input-individuals-searchKey')
 * @param {boolean} disabled - Whether search is disabled
 * @param {Object} project - Project context for watchlist operations
 * @param {string} initialSearchQuery - Initial search query from URL parameter (for restoration)
 * @param {boolean} showSearchButton - Whether to show a manual search button (default: false)
 * @param {boolean} disableAutoSearch - Whether to disable auto-search on typing (default: false)
 */
const IndividualsNameSearch = observer(
  ({
    inputId = 'ps-input-individuals-searchKey',
    disabled = false,
    project,
    initialSearchQuery = '',
    showSearchButton = false,
    disableAutoSearch = false
  }) => {
    const { profilerAccessStore, utilsStore, authStore } = useStore();
    const { navigate } = useHistory();

    const state = useLocalObservable(() => ({
      // TD-1709: Track current search query for URL restoration
      currentSearchQuery: initialSearchQuery || '',

      setCurrentSearchQuery: (query) => {
        state.currentSearchQuery = query;
        updateUrlSearchQuery(query);
      },

      /**
       * Navigate to individual details page
       * Uses URL-based navigation with individual ID in path and ref param for back button label.
       * The individual details page fetches data from API using the ID.
       *
       * @param {Object} individual - Individual data from search result
       * @param {Function} onFinally - Callback when navigation is complete
       * @param {Function} onSuccess - Custom success handler (e.g., for new tab)
       */
      navigateToIndividual: (individual, onFinally, onSuccess) => {
        const individualId = individual.personId || individual.crefoId || individual.id;

        if (onSuccess) {
          // Custom success handler (e.g., open in new tab)
          onSuccess({ individualId, ref: 'search results' });
        } else {
          // Navigate to standalone individual details page using URL-based approach
          const path = utilsStore.getIndividualDetailsPath(individualId, {
            ref: 'search results'
          });
          navigate(path);
        }
        onFinally?.();
      }
    }));

    const isSearchDisabled =
      (profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit) ||
      disabled;

    return (
      <ProfilerNameSearch
        apiEndpoint={API_ENDPOINTS.GET_PROFILER_INDIVIDUALS_BY_NAME}
        ResultItemComponent={IndividualsNameResultItem}
        resultItemProps={{
          onNavigateToIndividual: state.navigateToIndividual,
          utilsStore,
          inputId
        }}
        placeholder="Type individual name here"
        label="Individual name"
        inputId={inputId}
        disabled={isSearchDisabled}
        showSearchButton={showSearchButton}
        disableAutoSearch={disableAutoSearch}
        initialSearchKey={initialSearchQuery}
        onSearchKeyChange={state.setCurrentSearchQuery}
      />
    );
  }
);

export default IndividualsNameSearch;
